import { useMemo, useEffect } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { DashboardPage } from '~/components/ui/dashboard/DashboardPage';
import { canAccess } from '~/utils/rbac';
import { useEnsureDashboard } from '~/hooks/useEnsureDashboard';
import { ErrorComponent } from '~/routes/__root';
import { portfolioIdAtom } from '~/atoms/dashboardAtom';
import type { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

// Define which widgets are allowed on the analysis page
const ALLOWED_ANALYSIS_WIDGETS = [
  'forward-returns',
  'asset-breakdown-proposed',
  'asset-breakdown-current',
  'market-analysis',
  'news-impact-analysis',
  'correlation-cluster',
] as const;

/**
 * Default layout positions for analysis dashboard
 */
const ANALYSIS_LAYOUT = [
  { h: 10, w: 4, x: 0, y: 0 }, // forward-returns
  { h: 10, w: 4, x: 4, y: 0 }, // market-analysis
  { h: 10, w: 4, x: 8, y: 0 }, // news-impact-analysis
  { h: 12, w: 6, x: 0, y: 10 }, // asset-breakdown-current
  { h: 12, w: 6, x: 6, y: 10 }, // asset-breakdown-proposed
  { h: 16, w: 12, x: 0, y: 22 }, // correlation-cluster (full width)
];

const ANALYSIS_WIDGET_IDS = [
  'forward-returns',
  'market-analysis',
  'news-impact-analysis',
  'asset-breakdown-current',
  'asset-breakdown-proposed',
  'correlation-cluster',
];

/**
 * Creates widget configs for analysis dashboard with default layout
 * @param withUUID - If true, generates unique IDs with UUIDs; if false, uses simple widget IDs for reset matching
 */
const createAnalysisDashboardConfig = (withUUID = true): Config[] => {
  return ANALYSIS_LAYOUT.map((position, index) => ({
    ...position,
    id: withUUID
      ? `${ANALYSIS_WIDGET_IDS[index]}-widget-${crypto.randomUUID()}`
      : ANALYSIS_WIDGET_IDS[index],
  }));
};

function AnalysisComponent() {
  const { portfolioId } = Route.useParams();
  const setPortfolioId = useSetAtom(portfolioIdAtom);

  // Update portfolioId atom when route changes
  useEffect(() => {
    if (portfolioId) {
      setPortfolioId(portfolioId);
    }
  }, [portfolioId, setPortfolioId]);

  const initialAnalysisConfig = useMemo(
    () => createAnalysisDashboardConfig(true),
    [],
  );
  const analysisDefaultLayout = useMemo(
    () => createAnalysisDashboardConfig(false),
    [],
  );

  const { dashboardId, error } = useEnsureDashboard({
    type: 'analysis',
    name: 'Analysis Dashboard',
    initialConfig: initialAnalysisConfig,
    portfolioId,
  });

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <DashboardPage
      dashboardId={dashboardId || ''}
      breadcrumbLabel="Analysis"
      breadcrumbUrl={`/portfolio/${portfolioId}/analysis`}
      pageTaglinePrefix="Analysis"
      allowedWidgets={ALLOWED_ANALYSIS_WIDGETS}
      initialLayout={analysisDefaultLayout}
      emptyStateTitle="No Analysis Widgets Found"
      emptyStateMessage="This dashboard doesn't have any analysis widgets yet. Add some analysis widgets to get started."
      emptyStateButtonLabel="Add Analysis Widget"
    />
  );
}

const AnalysisRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <AnalysisComponent />;
};

export const Route = createFileRoute(
  '/_authLayout/portfolio/$portfolioId/analysis',
)({
  beforeLoad: async () => {
    if (!canAccess('dashboard')) {
      throw notFound();
    }
  },
  component: AnalysisRoute,
});
